"use client";

import { useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useSession } from "next-auth/react";
import Link from "next/link";

import FeaturedListing from "./featured";
import { Button } from "@/src/components/ui/button";
import { useAppDispatch, useAppSelector } from "@/src/redux/store";
import {
  getPublicListingsRequest,
  LISTING_VERIFICATION_STATUS,
  PUBLIC_LISTING_CATEGORIES,
} from "@/src/redux/listings/features";
import type { ListingsCategories } from "@/src/redux/listings/interface";

/**
|--------------------------------------------------
| Featured Listings Component
|--------------------------------------------------
*/
const FeaturedListings = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { data: session } = useSession();

  const { publicListings, homePageListings } = useAppSelector(
    (state) => state.listing
  );
  const { homePageSearchClicked } = useAppSelector((state) => state.utils);

  const searchParams = useSearchParams();
  const currentCategory = searchParams.get("category") || "all";

  const [listings, setListings] = useState<ListingsCategories>({});
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  /**
  |--------------------------------------------------
  | Category Options (Main + Subcategories)
  |--------------------------------------------------
  */
  const CATEGORY_OPTIONS = [
    { label: "ALL SPACES", value: "all", enumValue: PUBLIC_LISTING_CATEGORIES.ALL },
    {
      label: "RESIDENCES",
      value: "residence",
      enumValue: PUBLIC_LISTING_CATEGORIES.RESIDENCE,
      subcategories: [
        { label: "Beachfront Villas", value: "beachfront_villas", enumValue: PUBLIC_LISTING_CATEGORIES.BEACHFRONT_VILLAS },
        { label: "Apartments", value: "apartments", enumValue: PUBLIC_LISTING_CATEGORIES.APARTMENTS },
      ],
    },
    { label: "RETAIL (Pop-ups)", value: "retail", enumValue: PUBLIC_LISTING_CATEGORIES.RETAIL },
    {
      label: "EXPERIENCES",
      value: "experiences-section",
      enumValue: PUBLIC_LISTING_CATEGORIES.EXPERIENCES,
      subcategories: [
        { label: "Sightseeing Tours", value: "sightseeing_tours", enumValue: PUBLIC_LISTING_CATEGORIES.SIGHTSEEING_TOURS },
        { label: "Private Boat Cruise", value: "private_boat_cruise", enumValue: PUBLIC_LISTING_CATEGORIES.PRIVATE_BOAT_CRUISE },
        { label: "Other Experiences", value: "other_experiences", enumValue: PUBLIC_LISTING_CATEGORIES.OTHER_EXPERIENCES },
      ],
    },
    { label: "EVENT CENTERS", value: "event_center", enumValue: PUBLIC_LISTING_CATEGORIES.EVENT_CENTER },
    { label: "ICONIC SPACES", value: "iconic", enumValue: PUBLIC_LISTING_CATEGORIES.ICONIC },
  ];

  /**
  |--------------------------------------------------
  | Helpers
  |--------------------------------------------------
  */
  const getCurrentCategoryEnum = () => {
    // Check main categories
    const main = CATEGORY_OPTIONS.find((opt) => opt.value === currentCategory);
    if (main) return main.enumValue;

    // Check subcategories
    for (const option of CATEGORY_OPTIONS) {
      if (option.subcategories) {
        const sub = option.subcategories.find((s) => s.value === currentCategory);
        if (sub) return sub.enumValue;
      }
    }
    return PUBLIC_LISTING_CATEGORIES.ALL;
  };

  const toggleDropdown = (value: string) => {
    setOpenDropdown(openDropdown === value ? null : value);
  };

  const handleViewMoreClick = () => {
    router.push("/listings");
  };

  /**
  |--------------------------------------------------
  | Effects
  |--------------------------------------------------
  */
  useEffect(() => {
    if (publicListings?.data.categories) {
      setListings(publicListings.data.categories);
    }
  }, [publicListings]);

  // Initial fetch (all)
  useEffect(() => {
    const params = {
      location: "Nigeria",
      category: PUBLIC_LISTING_CATEGORIES.ALL,
      pagination: { page: 1, per_page: 6 },
      filters: { verification_status: LISTING_VERIFICATION_STATUS.APPROVED },
    };
    dispatch(getPublicListingsRequest(params));
  }, [dispatch]);

  // Refetch on category change (including "all")
  useEffect(() => {
    if (homePageSearchClicked) return;

    const params = {
      location: "Nigeria",
      category: getCurrentCategoryEnum(),
      pagination: { page: 1, per_page: 6 },
      filters: { verification_status: LISTING_VERIFICATION_STATUS.APPROVED },
    };

    dispatch(getPublicListingsRequest(params));
  }, [currentCategory, homePageSearchClicked, dispatch]);

  /**
  |--------------------------------------------------
  | Filter listings to render
  |--------------------------------------------------
  */
  const listingsToRender = (() => {
    if (homePageSearchClicked) {
      const searchListings = homePageListings?.listings || [];
      if (currentCategory === "all") return searchListings;

      const currentEnum = getCurrentCategoryEnum();
      return searchListings.filter((listing: any) => listing.category === currentEnum);
    }

    if (currentCategory === "all") {
      return Object.values(listings).flat();
    }

    const currentEnum = getCurrentCategoryEnum();
    return listings[currentEnum] || [];
  })();

  /**
  |--------------------------------------------------
  | Render
  |--------------------------------------------------
  */
  return (
    <section className="w-full pt-10 pb-16 border-b border-gray-muted flex flex-col text-center items-center text-dark-primary px-4 sm:px-6 lg:px-12 xl:px-20">
      {/* Heading */}
      <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl text-center tracking-tight mb-6">
        Featured <span className="font-display">Spaces</span>
      </h2>

      {/* Categories Nav */}
      <div className="w-full flex flex-col items-center mb-8">
        <nav className="flex items-center py-2.5 border-b bg-gray-50/50 rounded-lg  flex-wrap border-gray-100">
          {CATEGORY_OPTIONS.map(({ label, value, subcategories }) => {
            const isActive =
              value === currentCategory ||
              (subcategories && subcategories.some((s) => s.value === currentCategory));

            if (subcategories) {
              return (
                <div key={value} className="relative flex-shrink-0">
                  {/* Category with Dropdown */}
                  <Link
                    href={`?category=${value}`}
                    scroll={false}
                    onClick={() => toggleDropdown(value)}
                    className={`px-4 py-2 mx-1 text-xs font-medium whitespace-nowrap rounded-md transition-all duration-200 flex items-center gap-2 ${
                      isActive
                        ? "bg-white text-[#279A86] font-semibold"
                        : "text-gray-600 hover:text-[#279A86] hover:bg-white"
                    }`}
                  >
                    {label}
                    <svg
                      className={`w-3 h-3 transition-transform duration-200 ${
                        openDropdown === value ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </Link>

                  {/* Dropdown directly under category */}
                  {openDropdown === value && (
                    <div className="absolute left-0 top-full mt-2 bg-white border border-gray-200 rounded-xl shadow-xl z-50 min-w-[180px] overflow-hidden">
                      <div className="py-2">
                        {subcategories.map((sub, i) => (
                          <Link
                            key={sub.value}
                            href={`?category=${sub.value}`}
                            scroll={false}
                            onClick={() => setOpenDropdown(null)}
                            className={`block px-4 py-3 text-sm transition-all duration-150 ${
                              sub.value === currentCategory
                                ? "bg-blue-50 text-[#279A86] font-medium border-r-2 border-blue-500"
                                : "text-gray-700 hover:text-[#279A86] hover:bg-gray-50"
                            } ${i !== subcategories.length - 1 ? "border-b border-gray-100" : ""}`}
                          >
                            {sub.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            }

            // Normal category
            return (
              <Link
                key={value}
                href={`?category=${value}`}
                scroll={false}
                className={`flex-shrink-0 px-4 py-2 mx-1 text-xs font-medium whitespace-nowrap rounded-md transition-all duration-200 ${
                  isActive
                    ? "bg-white text-[#279A86] font-semibold"
                    : "text-gray-600 hover:bg-white hover:shadow-sm"
                }`}
              >
                {label}
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Listings */}
      <div className="my-4 w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-10 lg:gap-12 min-h-[400px]">
        {listingsToRender.length === 0 ? (
          <div className="text-center col-span-full text-gray-500 flex items-center justify-center">
            <p>No listings found for this category</p>
          </div>
        ) : (
          listingsToRender.map((listing, index) => (
            <FeaturedListing
              key={listing.id || index}
              listing={{
                img: listing.images[0],
                name: listing.name,
                description: listing.description,
                size: listing?.size,
                size_unit: listing?.size_unit,
                id: listing.id,
                price: listing?.base_price,
                interval: listing?.base_price_interval,
                discounts: Array.isArray(listing.discounts)
                  ? listing.discounts.map((e) => ({
                      enable_discount: e.enable_discount,
                      discount: e.discount,
                    }))
                  : [],
              }}
            />
          ))
        )}
      </div>

      {/* View More */}
      <Button
        onClick={handleViewMoreClick}
        className="px-5 py-2 text-sm sm:text-base border border-gray-muted rounded-full mt-6"
        variant="ghost"
      >
        VIEW MORE ↗
      </Button>
    </section>
  );
};

export default FeaturedListings;
